import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../../sidebar";
import { MainContainer, SideContainer } from "../../ViewAlerts/styles";
import { BackgroundPattern } from "../../ListAreas/styles";
import { guardianAlertService } from "../../../../services/guardianAlertService";
import { goToGuardianHub } from "../../../../utils/appNavigation";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import {
  ContentWrapper,
  HeaderSection,
  Title,
  ButtonContainerLeft,
  StyledButton,
  StyledTable,
  PaginationContainer,
  PageButton,
  // Modal
  Backdrop,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalMeta,
  ModalActions,
  TableActionButton,
  CloseButton,
  PdfViewer,
  // Inline styles
  loadingWrapperStyle,
  loadingSpinnerStyle,
  loadingTextStyle,
  rowsBarStyle,
 FilterLabel2,
 StyledSelect2,
  resultCountStyle,
  thIndexStyle,
  tdIndexStyle,
  emptyStateStyle,
  emptyStateIconStyle,
  ellipsisStyle,
  pageButtonActiveStyle,
  avatarStyle,
  userCellStyle,
  periodCellStyle,
  StyledSelect, StyledOption, StyledFilterButton,
  filterBarStyle,
  DesktopOnly,
  MobileCards,
  MobileCard,
  MobileCardTitle,
  MobileCardMeta,
  MobileCardActions,
} from "./styles";

// ── Avatar com iniciais ────────────────────────────────────────────────────────
const UserAvatar = ({ name }) => {
  const initials =
    name
      ?.split(" ")
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase() ?? "?";

  return <div style={avatarStyle}>{initials}</div>;
};

// ── Célula de usuário ──────────────────────────────────────────────────────────
const UserCell = ({ name }) => (
  <div style={userCellStyle}>
    <UserAvatar name={name} />
    <span>{name ?? "—"}</span>
  </div>
);

// ── Formatadores ───────────────────────────────────────────────────────────────
const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

const fmtDateTime = (isoStr) => {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return (
    d.toLocaleDateString("pt-BR") +
    " às " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
};

// ── Componente principal ───────────────────────────────────────────────────────
const AlertReportHistory = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const collapsed = useSelector((state) => state.sidebar);
  const { area_id } = useParams();
  const isAreaScoped = Boolean(area_id);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selected, setSelected] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleBack = () => {
    if (area_id) {
      navigate(`/area_details/${area_id}`);
      return;
    }
    goToGuardianHub(dispatch, navigate);
  };

  useEffect(() => {
    guardianAlertService
      .getAlertReports(area_id)
      .then((data) => {
        setReports(data);
      })
      .catch((err) => console.error("Erro ao carregar relatórios:", err))
      .finally(() => setLoading(false));
  }, [area_id]);

  // ── Carrega blob do PDF ao selecionar ────────────────────────────────────────
  useEffect(() => {
    if (!selected) {
      setPdfUrl(null);
      return;
    }

    setPdfLoading(true);
    guardianAlertService
      .downloadAlertReport(selected.id)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      })
      .catch((err) => console.error("Erro ao carregar PDF:", err))
      .finally(() => setPdfLoading(false));

    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [selected]);

  const handleDownload = async (report) => {
    try {
      const blob = await guardianAlertService.downloadAlertReport(report.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = report.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao baixar relatório:", err);
    }
  };

  const handleSelect = (report) => {
    setSelected(report);
  };

  const handleCloseModal = () => {
    setSelected(null);
    setPdfUrl(null);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  // ── Estado do filtro ── (só usado quando não está escopado por área)
  const [areaFilter, setAreaFilter] = useState("");

  // ── Lista de áreas únicas derivada dos dados ──────────────────────────────────
  const areaOptions = [...new Set(reports.map((r) => r.area_name).filter(Boolean))].sort();

  // ── Dados filtrados (substitui `reports` na paginação) ────────────────────────
  const filteredReports = areaFilter
    ? reports.filter((r) => r.area_name === areaFilter)
    : reports;

  // ── Atualize as variáveis de paginação para usar filteredReports ──────────────
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredReports.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);

  const loadingBlock = (
    <div style={loadingWrapperStyle}>
      <div style={loadingSpinnerStyle} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <span style={loadingTextStyle}>Carregando relatórios...</span>
    </div>
  );

  if (loading) {
    if (embedded) return loadingBlock;
    return (
      <MainContainer>
        <BackgroundPattern />
        <Sidebar />
        <SideContainer collapsed={collapsed}>{loadingBlock}</SideContainer>
      </MainContainer>
    );
  }

  const panel = (
    <>
      <ContentWrapper $embedded={embedded}>
        {!embedded && (
          <>
            <ButtonContainerLeft>
              <StyledButton type="button" onClick={handleBack}>
                Voltar
              </StyledButton>
            </ButtonContainerLeft>
            <HeaderSection>
              <Title>Relatórios de Alerta</Title>
            </HeaderSection>
          </>
        )}

        {!isAreaScoped && (
          <div style={filterBarStyle}>
            <StyledSelect
              value={areaFilter}
              aria-label="Filtrar por área"
              onChange={(e) => {
                setAreaFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <StyledOption value="">Todas as áreas</StyledOption>
              {areaOptions.map((area) => (
                <StyledOption key={area} value={area}>
                  {area}
                </StyledOption>
              ))}
            </StyledSelect>

            {areaFilter && (
              <StyledFilterButton
                type="button"
                onClick={() => {
                  setAreaFilter("");
                  setCurrentPage(1);
                }}
              >
                ✕ Limpar
              </StyledFilterButton>
            )}
          </div>
        )}

        <div style={rowsBarStyle}>
          <FilterLabel2>Por página:</FilterLabel2>
          <StyledSelect2
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(e.target.value)}
          >
            <StyledOption value={5}>5</StyledOption>
            <StyledOption value={10}>10</StyledOption>
            <StyledOption value={25}>25</StyledOption>
            <StyledOption value={50}>50</StyledOption>
          </StyledSelect2>
        </div>

        {filteredReports.length > 0 && (
          <div style={resultCountStyle}>
            {filteredReports.length} relatório
            {filteredReports.length !== 1 ? "s" : ""} encontrado
            {filteredReports.length !== 1 ? "s" : ""}
            {areaFilter && (
              <span>
                {" "}
                · filtrando por <strong>{areaFilter}</strong>
              </span>
            )}
          </div>
        )}

        <DesktopOnly>
          <StyledTable>
            <thead>
              <tr>
                <th style={thIndexStyle}></th>
                {!isAreaScoped && <th>Área</th>}
                <th>Período</th>
                <th>Gerado em</th>
                <th>Gerado por</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <tr>
                  <td colSpan={isAreaScoped ? 5 : 6}>
                    <div style={emptyStateStyle}>
                      <span style={emptyStateIconStyle}>📭</span>
                      <span>Nenhum relatório encontrado.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((report, index) => {
                  const absoluteIndex = indexOfFirstItem + index + 1;
                  return (
                    <tr key={report.id}>
                      <td style={tdIndexStyle}>{absoluteIndex}</td>
                      {!isAreaScoped && (
                        <td style={{ fontWeight: 600 }}>
                          {report.area_name ?? "—"}
                        </td>
                      )}
                      <td style={periodCellStyle}>
                        {report.start_date
                          ? `${fmtDate(report.start_date)} → ${fmtDate(report.end_date)}`
                          : "Período completo"}
                      </td>
                      <td>{fmtDateTime(report.created_at)}</td>
                      <td>
                        {report.is_automated
                          ? "Automático"
                          : report.generated_by_name ?? "—"}
                      </td>
                      <td>
                        <TableActionButton
                          type="button"
                          onClick={() => handleSelect(report)}
                        >
                          👁 Visualizar
                        </TableActionButton>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </StyledTable>
        </DesktopOnly>

        <MobileCards>
          {reports.length === 0 ? (
            <MobileCard>
              <MobileCardMeta>Nenhum relatório encontrado.</MobileCardMeta>
            </MobileCard>
          ) : (
            currentItems.map((report, index) => {
              const absoluteIndex = indexOfFirstItem + index + 1;
              return (
                <MobileCard key={`m-${report.id}`}>
                  <MobileCardTitle>
                    #{absoluteIndex}
                    {!isAreaScoped ? ` · ${report.area_name ?? "—"}` : ""}
                  </MobileCardTitle>
                  <MobileCardMeta style={periodCellStyle}>
                    {report.start_date
                      ? `${fmtDate(report.start_date)} → ${fmtDate(report.end_date)}`
                      : "Período completo"}
                  </MobileCardMeta>
                  <MobileCardMeta>
                    Gerado em: {fmtDateTime(report.created_at)}
                  </MobileCardMeta>
                  <MobileCardMeta>
                    {report.is_automated
                      ? "Automático"
                      : report.generated_by_name ?? "—"}
                  </MobileCardMeta>
                  <MobileCardActions>
                    <TableActionButton
                      type="button"
                      onClick={() => handleSelect(report)}
                    >
                      👁 Visualizar
                    </TableActionButton>
                  </MobileCardActions>
                </MobileCard>
              );
            })
          )}
        </MobileCards>

        {totalPages > 1 && (
          <PaginationContainer>
            <PageButton
              type="button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              <FiChevronLeft />
            </PageButton>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) =>
                  p === 1 ||
                  p === totalPages ||
                  Math.abs(p - currentPage) <= 1
              )
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} style={ellipsisStyle}>
                    …
                  </span>
                ) : (
                  <PageButton
                    key={p}
                    type="button"
                    onClick={() => setCurrentPage(p)}
                    style={p === currentPage ? pageButtonActiveStyle : {}}
                  >
                    {p}
                  </PageButton>
                )
              )}

            <PageButton
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              <FiChevronRight />
            </PageButton>
          </PaginationContainer>
        )}
      </ContentWrapper>

      {selected && (
        <Backdrop onClick={handleCloseModal}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>Relatório — {selected.area}</ModalTitle>
                <ModalMeta>
                  Gerado em {fmtDateTime(selected.created_at)} por{" "}
                  {selected.is_automated
                    ? "Automático"
                    : selected.generated_by_name}
                </ModalMeta>
              </div>
              <ModalActions>
                <StyledButton onClick={() => handleDownload(selected)}>
                  ⬇ Download PDF
                </StyledButton>
                <CloseButton onClick={handleCloseModal}>✕</CloseButton>
              </ModalActions>
            </ModalHeader>

            <PdfViewer>
              {pdfLoading ? (
                <div style={loadingWrapperStyle}>
                  <div style={loadingSpinnerStyle} />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  <span style={loadingTextStyle}>Carregando PDF...</span>
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                  width="100%"
                  height="100%"
                  style={{ border: "none", display: "block" }}
                  title="Relatório PDF"
                />
              ) : (
                <div style={emptyStateStyle}>
                  <span style={emptyStateIconStyle}>⚠️</span>
                  <span>Não foi possível carregar o PDF.</span>
                </div>
              )}
            </PdfViewer>
          </Modal>
        </Backdrop>
      )}
    </>
  );

  if (embedded) return panel;

  return (
    <MainContainer>
      <BackgroundPattern />
      <Sidebar />
      <SideContainer collapsed={collapsed}>{panel}</SideContainer>
    </MainContainer>
  );
};

export default AlertReportHistory;
